import type { Rive } from '@rive-app/react-canvas';
import type { TFunction } from 'i18next';
import maxBy from 'lodash/maxBy';

import type { BannerToc } from '../../../../services/ui';
import { isLiveTocForLobbyBanner } from '../../../../stores/tournament/liveTocLobbyBanner';
import { tocRiveUrl } from '../../../../stores/tournament/tocBannerRive';
import type { TournamentOfChampions } from '../../../../stores/tournament/types';
import { formatFinalPool } from '../../../Dialogs/TournamentDialog/ChampionsTournament/ChampionsBanner/buildBannerV6Values';

type Vm = NonNullable<Rive['viewModelInstance']>;

export type LobbyToc = Omit<BannerToc, 'validUntil'>;

export interface TocLobbyBannerValues {
  finalPoolCc: string;
  finalPoolSc: string;
  currentRound: number;
  totalRounds: number;
}

/** The lobby banner's raw `toc` values as the file binds them: final pools in the coin formatting, rounds as-is. */
export function buildTocLobbyBannerValues(values: LobbyToc, t: TFunction, language?: string): TocLobbyBannerValues {
  const { finalCc, finalSc } = formatFinalPool({ gcCoin: values.finalPoolGc, scCoin: values.finalPoolSc }, t, language);
  // Rounds left and trophies are derived by the file from these.
  return {
    finalPoolCc: finalCc,
    finalPoolSc: finalSc,
    currentRound: values.currentRound,
    totalRounds: values.totalRounds,
  };
}

/** A round the player can play now, as the lobby `toc`; null otherwise. Joined or not: the round is open to them. */
export function lobbyTocFromLiveToc(toc: TournamentOfChampions | null): LobbyToc | null {
  if (!isLiveTocForLobbyBanner(toc)) return null;
  const finalPool = maxBy(toc.rounds, 'roundIndex')?.prizePool;
  return {
    desktopRiveUrl: tocRiveUrl(toc.assets?.rives, 'lobbyBannerDesktop'),
    mobileRiveUrl: tocRiveUrl(toc.assets?.rives, 'lobbyBannerMobile'),
    finalPoolGc: finalPool?.gcCoin ?? 0,
    finalPoolSc: finalPool?.scCoin ?? 0,
    currentRound: Math.min(toc.currentRoundIndex + 1, toc.rounds.length),
    totalRounds: toc.rounds.length,
  };
}

// A property the file lacks reads as null: skipped silently.
export function bindTocLobbyBanner(vm: Vm, values: TocLobbyBannerValues) {
  const setString = (path: string, value: string) => {
    const prop = vm.string?.(path);
    if (prop) prop.value = value;
  };
  const setNumber = (path: string, value: number) => {
    const prop = vm.number?.(path);
    if (prop) prop.value = value;
  };
  setString('finalPoolCc', values.finalPoolCc);
  setString('finalPoolSc', values.finalPoolSc);
  setNumber('currentRound', values.currentRound);
  setNumber('totalRounds', values.totalRounds);
}
